import {
  Download,
  History,
  ImagePlus,
  Link2,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ImageGenerationPreview, { ImageGenerationStatus } from '../components/ImageGenerationPreview';
import MaskEditor, { MaskArea } from '../components/MaskEditor';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useQuoteSocket } from '../hooks/useSocket';
import { RenderUpdateEvent } from '../lib/socket';
import {
  formatCurrency,
  getRoomTypeLabel,
  RenderGenerationStatus,
  RestyleGenerationParams,
  RestyleMaskData,
  RestyleProject,
  RoomType,
} from '../types/quote';

type Phase = 'source' | 'edit' | 'results';

interface AssetTemplate {
  id: string;
  name: string;
  base_price?: number;
  images?: Array<{ id: string; url: string; storage_key?: string }>;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

const STYLE_OPTIONS = ['modern', 'scandinavian', 'industrial', 'traditional', 'coastal', 'bohemian'];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const Restyle: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [phase, setPhase] = useState<Phase>('source');
  const [listingUrl, setListingUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [scrapeTitle, setScrapeTitle] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [project, setProject] = useState<RestyleProject | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<RestyleProject[]>([]);
  const [placementMode, setPlacementMode] = useState<'auto' | 'manual'>('auto');
  const [maskAreas, setMaskAreas] = useState<MaskArea[]>([]);
  const [activeAreaId, setActiveAreaId] = useState<string | null>(null);
  const [stylePreset, setStylePreset] = useState('modern');
  const [roomType, setRoomType] = useState<RoomType>(RoomType.LIVING_ROOM);
  const [furniture, setFurniture] = useState<AssetTemplate[]>([]);
  const [furnitureSearch, setFurnitureSearch] = useState('');
  const [generating, setGenerating] = useState(false);
  const [activeRenderId, setActiveRenderId] = useState<string | null>(null);
  const [variantBase, setVariantBase] = useState<'original' | string>('original');
  const [error, setError] = useState<string | null>(null);
  const exportMaskRef = useRef<(() => Promise<{ blob: Blob; maskData: import('../types/quote').RestyleMaskData }>) | null>(null);
  const pendingRenderRef = useRef<string | null>(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const loadHistory = useCallback(async () => {
    const res = await fetch('/api/restyle/projects?limit=30', { headers: authHeaders() });
    const data = await res.json();
    if (data.success) setHistory(data.data || []);
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadFurniture = useCallback(async () => {
    const params = new URLSearchParams({ page: '1', limit: '24', is_active: 'true', room_type: roomType });
    if (furnitureSearch) params.set('search', furnitureSearch);
    const res = await fetch(`/api/asset-templates?${params}`, { headers: authHeaders() });
    const data = await res.json();
    if (data.success) setFurniture(data.data || []);
  }, [roomType, furnitureSearch]);

  useEffect(() => {
    if (phase === 'edit') loadFurniture();
  }, [phase, loadFurniture]);

  const handleRenderUpdate = useCallback((event: RenderUpdateEvent) => {
    setProject(prev => {
      if (!prev?.room || prev.room.id !== event.roomId) return prev;
      const renders = (prev.room.renders || []).map(r =>
        r.id === event.renderId
          ? {
              ...r,
              status: event.status === 'completed' ? RenderGenerationStatus.COMPLETED : event.status === 'failed' ? RenderGenerationStatus.FAILED : RenderGenerationStatus.PROCESSING,
              image_url: event.imageUrl || r.image_url,
              error_message: event.error,
            }
          : r
      );
      if (!renders.find(r => r.id === event.renderId) && event.imageUrl) {
        renders.push({
          id: event.renderId,
          quote_room_id: prev.room.id,
          status: RenderGenerationStatus.COMPLETED,
          image_url: event.imageUrl,
          is_selected: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as any);
      }
      return { ...prev, room: { ...prev.room, renders } };
    });

    if (event.status === 'completed' || event.status === 'failed') {
      pendingRenderRef.current = null;
      setGenerating(false);
      if (event.status === 'completed') setPhase('results');
      if (event.status === 'failed') {
        setError(event.error || 'Generation failed');
        setPhase('results');
      }
    }
  }, []);

  const pollRenderCompletion = useCallback(async (projectId: string, renderId: string) => {
    for (let i = 0; i < 40; i++) {
      await new Promise(r => setTimeout(r, 3000));
      if (pendingRenderRef.current !== renderId) return;

      const res = await fetch(`/api/restyle/projects/${projectId}`, { headers: authHeaders() });
      const data = await res.json();
      if (!data.success) continue;

      const render = data.data.room?.renders?.find((r: { id: string }) => r.id === renderId);
      if (render?.status !== RenderGenerationStatus.COMPLETED && render?.status !== RenderGenerationStatus.FAILED) {
        continue;
      }

      pendingRenderRef.current = null;
      setProject(data.data);
      setGenerating(false);
      setPhase('results');
      if (render.status === RenderGenerationStatus.COMPLETED) return;
      setError(render.error_message || 'Generation failed');
      return;
    }

    if (pendingRenderRef.current === renderId) {
      pendingRenderRef.current = null;
      setGenerating(false);
      setError('Generation timed out — check History for results');
    }
  }, []);

  useQuoteSocket(project?.id, handleRenderUpdate);

  const scrape = async () => {
    if (!listingUrl.trim()) return;
    setScraping(true);
    setError(null);
    try {
      const res = await fetch('/api/restyle/scrape', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: listingUrl.trim() }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Scrape failed');
      setImages(data.data.images || []);
      setFailedImages(new Set());
      setScrapeTitle(data.data.title || '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scrape failed');
    } finally {
      setScraping(false);
    }
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/restyle/upload', { method: 'POST', headers: authHeaders(), body: form });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Upload failed');
      setImages([data.data.url]);
      setSelectedImage(data.data.url);
      setScrapeTitle(file.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const startProject = async (imageUrl: string, storageKey?: string) => {
    setError(null);
    const res = await fetch('/api/restyle/projects', {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        listing_url: listingUrl || undefined,
        source_image_url: imageUrl,
        source_storage_key: storageKey,
        title: scrapeTitle || 'Restyle Project',
        room_type: roomType,
      }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to create project');
    setProject(data.data);
    setMaskAreas([{ id: crypto.randomUUID(), color: '#3b82f6', strokes: [] }]);
    setActiveAreaId(null);
    setPhase('edit');
    loadHistory();
  };

  const selectImage = async (url: string) => {
    setSelectedImage(url);
    try {
      await startProject(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start project');
    }
  };

  const openProject = async (id: string) => {
    setError(null);
    const res = await fetch(`/api/restyle/projects/${id}`, { headers: authHeaders() });
    const data = await res.json();
    if (!data.success) {
      setError(data.message || 'Failed to load project');
      return;
    }
    setProject(data.data);
    setRoomType(data.data.room?.room_type || RoomType.LIVING_ROOM);
    const maskData = data.data.room?.crop_data;
    if (maskData?.placement_mode) setPlacementMode(maskData.placement_mode);
    setMaskAreas([{ id: crypto.randomUUID(), color: '#3b82f6', strokes: [] }]);
    setHistoryOpen(false);
    setPhase(data.data.room?.renders?.length ? 'results' : 'edit');
  };

  const addFurniture = async (templateId: string) => {
    if (!project?.id) return;
    const res = await fetch(`/api/restyle/projects/${project.id}/items`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ asset_template_id: templateId }),
    });
    const data = await res.json();
    if (data.success) {
      setProject(data.data);
      if (placementMode === 'manual' && activeAreaId) {
        setMaskAreas(prev => prev.map(a => (a.id === activeAreaId ? { ...a, asset_template_id: templateId } : a)));
      }
    }
  };

  const removeFurniture = async (itemId: string) => {
    if (!project?.id) return;
    const res = await fetch(`/api/restyle/projects/${project.id}/items/${itemId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    const data = await res.json();
    if (data.success) setProject(data.data);
  };

  const startGeneration = async (params: RestyleGenerationParams) => {
    if (!project?.id) return;
    const genRes = await fetch(`/api/restyle/projects/${project.id}/generate`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const genData = await genRes.json();
    if (!genData.success) throw new Error(genData.message || 'Generation failed');

    const renderId = genData.data.id as string;
    pendingRenderRef.current = renderId;
    setActiveRenderId(renderId);

    setProject(prev => {
      if (!prev?.room) return prev;
      return {
        ...prev,
        room: { ...prev.room, renders: [...(prev.room.renders || []), genData.data] },
      };
    });

    void pollRenderCompletion(project.id, renderId);
  };

  const startNewVariant = async () => {
    if (!project?.id) return;
    setError(null);
    try {
      const res = await fetch(`/api/restyle/projects/${project.id}/editor-base`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          render_id: variantBase === 'original' ? null : variantBase,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to prepare editor');

      const maskData = data.data.room?.crop_data as RestyleMaskData | undefined;
      if (maskData?.placement_mode) setPlacementMode(maskData.placement_mode);
      setMaskAreas([{ id: crypto.randomUUID(), color: '#3b82f6', strokes: [] }]);
      setActiveAreaId(null);
      setProject(data.data);
      setPhase('edit');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start new variant');
    }
  };

  const saveMaskAndGenerate = async () => {
    if (!project?.id || !exportMaskRef.current) {
      setError('Editor not ready — try again in a moment');
      return;
    }
    setGenerating(true);
    setPhase('results');
    setError(null);
    try {
      const { blob, maskData } = await exportMaskRef.current();
      if (placementMode === 'manual') {
        maskData.areas = maskData.areas.map(a => ({
          ...a,
          asset_template_id: maskAreas.find(m => m.id === a.id)?.asset_template_id,
        }));
      }

      const form = new FormData();
      form.append('file', blob, 'mask.png');
      form.append('mask_data', JSON.stringify({ ...maskData, placement_mode: placementMode }));

      const maskRes = await fetch(`/api/restyle/projects/${project.id}/mask`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      const maskResult = await maskRes.json();
      if (!maskResult.success) throw new Error(maskResult.message || 'Failed to save mask');
      setProject(maskResult.data);

      await startGeneration({
        style: [stylePreset],
        placement_mode: placementMode,
        areas: maskData.areas,
      });
    } catch (e) {
      setGenerating(false);
      setPhase('edit');
      setError(e instanceof Error ? e.message : 'Generation failed');
    }
  };

  const selectRender = async (renderId: string) => {
    if (!project?.id) return;
    setActiveRenderId(renderId);
    const render = project.room?.renders?.find(r => r.id === renderId);
    if (render?.status === RenderGenerationStatus.COMPLETED && render.image_url) {
      setVariantBase(renderId);
    }
    const res = await fetch(`/api/restyle/projects/${project.id}/renders/${renderId}/select`, {
      method: 'PATCH',
      headers: authHeaders(),
    });
    const data = await res.json();
    if (data.success) setProject(data.data);
  };

  const roomImageUrl = project?.room?.cropped_image_url || project?.room?.source_image_url || selectedImage;
  const originalImageUrl = project?.room?.original_image_url || roomImageUrl;
  const editorBaseRenderId = (project?.room?.crop_data as RestyleMaskData | undefined)?.editor_base_render_id;

  const selectedRender =
    project?.room?.renders?.find(r => r.is_selected) ||
    project?.room?.renders?.find(r => r.status === RenderGenerationStatus.COMPLETED);

  const previewRender =
    (activeRenderId && project?.room?.renders?.find(r => r.id === activeRenderId)) ||
    selectedRender ||
    project?.room?.renders?.[project.room.renders.length - 1];

  const previewStatus: ImageGenerationStatus = generating
    ? 'generating'
    : previewRender?.image_url
      ? 'complete'
      : 'idle';

  const completedRenders =
    project?.room?.renders?.filter(
      r => r.status === RenderGenerationStatus.COMPLETED && r.image_url
    ) ?? [];

  const variantBaseImageUrl =
    variantBase === 'original'
      ? originalImageUrl
      : completedRenders.find(r => r.id === variantBase)?.image_url || originalImageUrl;

  const editorBaseLabel = editorBaseRenderId
    ? `Variant ${completedRenders.findIndex(r => r.id === editorBaseRenderId) + 1}`
    : 'Original listing photo';

  const previewBaseRenderId =
    previewRender?.generation_params?.editor_base_render_id ?? editorBaseRenderId;

  const comparisonBaseImageUrl = previewBaseRenderId
    ? completedRenders.find(r => r.id === previewBaseRenderId)?.image_url || roomImageUrl
    : originalImageUrl;

  const comparisonBaseTitle = previewBaseRenderId
    ? `Variant ${completedRenders.findIndex(r => r.id === previewBaseRenderId) + 1}`
    : 'Original';

  const convertToQuote = async () => {
    if (!project?.id) return;
    const res = await fetch(`/api/restyle/projects/${project.id}/convert`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_name: project.property_name || 'Restyle Customer',
        property_name: project.property_name,
        property_address: project.property_address,
      }),
    });
    const data = await res.json();
    if (data.success) {
      loadHistory();
      navigate(`/quotes/${project.id}/edit`);
    }
    else setError(data.message || 'Convert failed');
  };

  const resetNew = () => {
    setProject(null);
    setSelectedImage(null);
    setImages([]);
    setListingUrl('');
    setPhase('source');
    setMaskAreas([]);
    setVariantBase('original');
    setActiveRenderId(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="restyle" />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              Restyle a Listing
            </h1>
          </div>
          <Button variant="outline" onClick={() => { setHistoryOpen(true); loadHistory(); }}>
            <History className="h-4 w-4 mr-2" /> History
          </Button>
        </div>

        <Tabs value={phase} onValueChange={v => setPhase(v as Phase)} className="mb-6">
          <TabsList>
            <TabsTrigger value="source">Source</TabsTrigger>
            <TabsTrigger value="edit" disabled={!project}>Edit</TabsTrigger>
            <TabsTrigger value="results" disabled={!project}>Results</TabsTrigger>
          </TabsList>
        </Tabs>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
        )}

        {phase === 'source' && (
          <Card>
            <CardHeader>
              <CardTitle>Choose a room photo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="url">
                <TabsList>
                  <TabsTrigger value="url"><Link2 className="h-4 w-4 mr-1" /> From URL</TabsTrigger>
                  <TabsTrigger value="upload"><ImagePlus className="h-4 w-4 mr-1" /> Upload</TabsTrigger>
                </TabsList>
                <TabsContent value="url" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Paste Rightmove, Zoopla, or listing URL"
                      value={listingUrl}
                      onChange={e => setListingUrl(e.target.value)}
                    />
                    <Button onClick={scrape} disabled={scraping || !listingUrl.trim()}>
                      {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fetch'}
                    </Button>
                  </div>
                </TabsContent>
                <TabsContent value="upload" className="mt-4">
                  <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/50">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0])}
                    />
                    {uploading ? (
                      <Loader2 className="h-8 w-8 animate-spin" />
                    ) : (
                      <>
                        <ImagePlus className="h-10 w-10 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Drag & drop or click to upload</p>
                      </>
                    )}
                  </label>
                </TabsContent>
              </Tabs>

              {scrapeTitle && <p className="text-sm font-medium">{scrapeTitle}</p>}

              {images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {images.filter(url => !failedImages.has(url)).map(url => (
                    <button
                      key={url}
                      type="button"
                      className={`relative aspect-video rounded-lg overflow-hidden border-2 hover:border-primary ${selectedImage === url ? 'border-primary ring-2 ring-primary' : 'border-transparent'}`}
                      onClick={() => selectImage(url)}
                    >
                      <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={() => setFailedImages(prev => new Set(prev).add(url))}
                      />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {phase === 'edit' && project && roomImageUrl && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setPhase(project.room?.renders?.length ? 'results' : 'source')}>
                ← Back
              </Button>
              {editorBaseRenderId && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                  Base: {editorBaseLabel}
                </span>
              )}
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                <Button size="sm" variant={placementMode === 'auto' ? 'default' : 'ghost'} onClick={() => setPlacementMode('auto')}>AI Places</Button>
                <Button size="sm" variant={placementMode === 'manual' ? 'default' : 'ghost'} onClick={() => setPlacementMode('manual')}>I'll Place</Button>
              </div>
              <Select value={roomType} onValueChange={v => setRoomType(v as RoomType)}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(RoomType).map(t => (
                    <SelectItem key={t} value={t}>{getRoomTypeLabel(t)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={stylePreset} onValueChange={setStylePreset}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Style" /></SelectTrigger>
                <SelectContent>
                  {STYLE_OPTIONS.map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Paint furniture to replace</CardTitle></CardHeader>
                <CardContent>
                  <MaskEditor
                    key={roomImageUrl}
                    imageUrl={roomImageUrl}
                    placementMode={placementMode}
                    areas={maskAreas}
                    activeAreaId={activeAreaId}
                    onAreasChange={setMaskAreas}
                    onActiveAreaChange={setActiveAreaId}
                    onExportReady={fn => { exportMaskRef.current = fn; }}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Homebound furniture</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <Input placeholder="Search..." value={furnitureSearch} onChange={e => setFurnitureSearch(e.target.value)} />
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {furniture.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        className="flex gap-2 p-2 border rounded text-left hover:bg-muted"
                        onClick={() => addFurniture(item.id)}
                      >
                        <div className="w-12 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                          {item.images?.[0]?.url && (
                            <img
                              src={item.images[0].url}
                              alt=""
                              className="w-full h-full object-contain"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(item.base_price || 0)}/mo</p>
                        </div>
                      </button>
                    ))}
                  </div>

                  {placementMode === 'manual' && maskAreas.some(a => a.strokes.length) && (
                    <div className="text-xs space-y-1 border-t pt-3">
                      <p className="font-medium">Area assignments</p>
                      {maskAreas.filter(a => a.strokes.length).map((a, i) => (
                        <p key={a.id} style={{ color: a.color }}>
                          Area {i + 1}: {project.room?.items?.find(it => it.asset_template_id === a.asset_template_id)?.asset_template?.name || 'Pick furniture'}
                        </p>
                      ))}
                    </div>
                  )}

                  {project.room?.items && project.room.items.length > 0 && (
                    <div className="space-y-2 border-t pt-3">
                      <p className="text-sm font-medium">Selected ({project.room.items.length})</p>
                      {project.room.items.map(item => (
                        <div key={item.id} className="flex justify-between items-center text-sm">
                          <span>{item.asset_template?.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeFurniture(item.id)}>Remove</Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button className="w-full" disabled={generating || !project.room?.items?.length} onClick={saveMaskAndGenerate}>
                    {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Generate Restyle
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {phase === 'results' && project && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <Button variant="ghost" onClick={() => setPhase('edit')}>← Back to editor</Button>
              <Button variant="outline" onClick={convertToQuote}>Convert to Quote</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">{comparisonBaseTitle}</CardTitle></CardHeader>
                <CardContent>
                  {comparisonBaseImageUrl && (
                    <img src={comparisonBaseImageUrl} alt={comparisonBaseTitle} className="w-full rounded-lg" />
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Restyled</CardTitle></CardHeader>
                <CardContent>
                  <ImageGenerationPreview
                    status={previewStatus}
                    imageUrl={previewRender?.image_url}
                  />
                </CardContent>
              </Card>
            </div>

            {project.room?.renders && project.room.renders.length > 0 && (
              <div className="space-y-3">
                <Label className="block">Variations</Label>
                <div className="flex gap-2 flex-wrap">
                  {project.room.renders.map(render => (
                    <button
                      key={render.id}
                      type="button"
                      className={`w-20 h-20 rounded border-2 overflow-hidden ${render.is_selected || render.id === activeRenderId ? 'border-primary' : 'border-transparent'}`}
                      onClick={() => selectRender(render.id)}
                    >
                      {render.image_url ? (
                        <img src={render.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-muted/60" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Base for new variant</Label>
                    <Select value={variantBase} onValueChange={setVariantBase}>
                      <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder="Choose base image" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="original">Original listing photo</SelectItem>
                        {completedRenders.map((render, index) => (
                          <SelectItem key={render.id} value={render.id}>
                            Variant {index + 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {variantBaseImageUrl && variantBase !== 'original' && (
                    <img
                      src={variantBaseImageUrl}
                      alt="Variant base preview"
                      className="w-14 h-14 rounded border object-cover"
                    />
                  )}
                  <Button
                    variant="outline"
                    disabled={generating || (variantBase !== 'original' && !completedRenders.some(r => r.id === variantBase))}
                    onClick={() => void startNewVariant()}
                  >
                    + New variant
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              {previewRender?.image_url && !generating && (
                <Button asChild variant="outline">
                  <a href={previewRender.image_url} download target="_blank" rel="noreferrer">
                    <Download className="h-4 w-4 mr-2" /> Download HD
                  </a>
                </Button>
              )}
              <Button variant="secondary" onClick={resetNew}>New Listing</Button>
            </div>
          </div>
        )}

        {historyOpen && (
          <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setHistoryOpen(false)}>
            <div
              className="absolute right-0 top-0 h-full w-full max-w-md bg-background shadow-xl p-4 overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold">Restyle History</h2>
                <Button variant="ghost" size="icon" onClick={() => setHistoryOpen(false)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-3">
                {history.map(h => (
                  <button
                    key={h.id}
                    type="button"
                    className="w-full flex gap-3 p-3 border rounded-lg text-left hover:bg-muted"
                    onClick={() => openProject(h.id)}
                  >
                    <div className="w-16 h-16 bg-muted rounded overflow-hidden flex-shrink-0">
                      {(h.thumbnail_url || h.room?.cropped_image_url) && (
                        <img src={h.thumbnail_url || h.room?.cropped_image_url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{h.property_name || h.quote_number}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(h.created_at).toLocaleDateString()} · {h.renders_count || 0} renders
                        {h.restyle_converted_at && ' · Converted to quote'}
                      </p>
                    </div>
                  </button>
                ))}
                {!history.length && <p className="text-sm text-muted-foreground">No restyle projects yet</p>}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Restyle;
